import { environment } from '@/config/environment';
import { isBot } from '@/lib/user-agent';
import {
  getDemoFingerprint,
  websiteBrowserContext,
} from '@/lib/demo-fingerprint';
import type {
  WebsiteSessionEventNameType,
  WebsiteSessionHeartbeatType,
  WebsiteTelemetryResponseType,
} from './session-replay.model';
import {
  SessionActivityAccumulator,
  WebsiteSessionEventQueue,
  getOrCreateStoredUuid,
  getWebsitePagePath,
  observeWebsiteNavigation,
} from './session-replay.util';
import { WebsiteReplayRecorder } from './website-replay-recorder';

const SESSION_UUID_STORAGE_KEY = 'ob.website-device-session-uuid';
const SESSION_STARTED_STORAGE_KEY = 'ob.website-session-started';
const MAX_EARLY_EVENTS = 20;
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

/** utm_* query values; plain parsing so it works wherever `location` does. */
function utmParameters(search: string): Record<string, string> {
  const utm: Record<string, string> = {};
  for (const pair of search.replace(/^\?/, '').split('&')) {
    const [rawKey, rawValue = ''] = pair.split('=');
    const key = decodeQueryPart(rawKey) as (typeof UTM_KEYS)[number];
    if (!UTM_KEYS.includes(key) || utm[key]) continue;
    const value = decodeQueryPart(rawValue);
    if (value) utm[key] = value.slice(0, 200);
  }
  return utm;
}

function decodeQueryPart(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

function referrerHost(referrer: string): string | null {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname;
  } catch {
    return null;
  }
}
const DEVICE_UUID_STORAGE_KEY = 'ob.website-device-uuid';
const HEARTBEAT_INTERVAL_MS = 30_000;
const ACTIVE_WINDOW_MS = 30_000;
const EVENT_FLUSH_DELAY_MS = 250;

const events = new WebsiteSessionEventQueue();
let lastPagePath: string | null = null;

class WebsiteSessionReplayService {
  private sessionUuid = '';
  /** Server-confirmed customer_device_session_uuid; null until telemetry is on. */
  private serverSessionUuid: string | null = null;
  /** Interactions recorded before the first heartbeat says telemetry is on
   *  (e.g. a demo visible on load). Replayed once enabled, dropped if not. */
  private earlyEvents: {
    type: WebsiteSessionEventNameType;
    value: string | null;
    data: Record<string, unknown> | null;
    pagePath: string;
    url: string;
  }[] = [];
  private deviceUuid = '';
  private activity: SessionActivityAccumulator | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private inactiveTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private captureAllowed = false;
  private sending = false;
  private eventTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatRequested = false;
  private stopObservingNavigation: (() => void) | null = null;
  private readonly replay = new WebsiteReplayRecorder(
    environment.api.phpApiUrl,
    () => {
      events.clear();
      this.stop();
    }
  );

  private readonly handleActivity = (): void => {
    if (!this.activity) return;
    this.activity.setActive(true, Date.now());
    if (this.inactiveTimer) clearTimeout(this.inactiveTimer);
    this.inactiveTimer = setTimeout(() => {
      this.activity?.setActive(false, Date.now());
    }, ACTIVE_WINDOW_MS);
  };

  private readonly handleVisibilityChange = (): void => {
    if (!this.activity) return;
    const visible = !document.hidden;
    this.activity.setVisible(visible, Date.now());
    if (visible) {
      if (this.captureAllowed) this.replay.resume();
      this.handleActivity();
    } else {
      this.replay.pause();
      void this.sendHeartbeat(true);
    }
  };

  private readonly handlePageHide = (): void => {
    this.activity?.setVisible(false, Date.now());
    this.replay.pause();
    void this.sendHeartbeat(true);
  };

  private readonly handlePageShow = (): void => {
    this.handleVisibilityChange();
    this.recordPageView();
    void this.sendHeartbeat();
  };

  recordPageView(): void {
    const pagePath = getWebsitePagePath();
    if (!this.isCapturing() || pagePath === lastPagePath) return;
    lastPagePath = pagePath;
    events.add('page_view', pagePath, {
      url: window.location.href,
      title: document.title,
      referrer: document.referrer,
    });
    this.replay.recordPageView(pagePath);
    this.requestHeartbeat();
  }

  /** The telemetry session id the AI chats pass on, so every AI turn in this
   *  browsing session lands in one Lark thread. Null while telemetry is off. */
  getSessionUuid(): string | null {
    return this.isCapturing() ? this.serverSessionUuid : null;
  }

  /** Attribution, once per browsing session: where the visitor landed from.
   *  A reload keeps the same session, so a sessionStorage flag stops repeats. */
  private recordSessionStarted(): void {
    const flagKey = `${SESSION_STARTED_STORAGE_KEY}:${this.sessionUuid}`;
    try {
      if (sessionStorage.getItem(flagKey)) return;
      sessionStorage.setItem(flagKey, '1');
    } catch {
      // Storage blocked: record it; a rare duplicate beats losing attribution.
    }
    const utm = utmParameters(window.location.search);
    this.recordEvent('session_started', getWebsitePagePath(), {
      referrer: document.referrer || null,
      referrerHost: referrerHost(document.referrer),
      ...utm,
      language: document.documentElement?.lang || null,
    });
  }

  recordEvent(
    type: WebsiteSessionEventNameType,
    value: string | null,
    data: Record<string, unknown> | null,
    at?: { pagePath: string; url: string }
  ): void {
    if (!this.isCapturing()) {
      if (this.started && this.earlyEvents.length < MAX_EARLY_EVENTS) {
        this.earlyEvents.push({
          type,
          value,
          data,
          pagePath: getWebsitePagePath(),
          url: window.location.href,
        });
      }
      return;
    }
    const pagePath = at?.pagePath ?? getWebsitePagePath();
    const eventData = {
      ...data,
      pagePath,
      url: at?.url ?? window.location.href,
    };
    events.add(type, value ?? pagePath, eventData);
    this.replay.addCustomEvent(value ? `${type}: ${value}` : type, {
      ...eventData,
      value,
    });
    this.requestHeartbeat();
  }

  private requestHeartbeat(): void {
    if (this.eventTimer) return;
    this.eventTimer = setTimeout(() => {
      this.eventTimer = null;
      void this.sendHeartbeat();
    }, EVENT_FLUSH_DELAY_MS);
  }

  start(): void {
    if (
      this.started ||
      !environment.analytics.enabled ||
      navigator.webdriver ||
      isBot()
    ) {
      return;
    }
    this.started = true;
    this.captureAllowed = false;
    events.clear();
    lastPagePath = null;
    this.replay.disable();
    this.sessionUuid = getOrCreateStoredUuid(
      sessionStorage,
      SESSION_UUID_STORAGE_KEY
    );
    this.deviceUuid = getOrCreateStoredUuid(
      localStorage,
      DEVICE_UUID_STORAGE_KEY
    );
    this.activity = new SessionActivityAccumulator(
      Date.now(),
      !document.hidden
    );
    this.handleActivity();

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('pageshow', this.handlePageShow);
    this.stopObservingNavigation = observeWebsiteNavigation(() =>
      this.recordPageView()
    );
    window.addEventListener('pointerdown', this.handleActivity, {
      passive: true,
    });
    window.addEventListener('keydown', this.handleActivity);
    window.addEventListener('scroll', this.handleActivity, { passive: true });
    window.addEventListener('touchstart', this.handleActivity, {
      passive: true,
    });

    this.heartbeatTimer = setInterval(
      () => void this.sendHeartbeat(),
      HEARTBEAT_INTERVAL_MS
    );
    void this.sendHeartbeat();
  }

  isCapturing(): boolean {
    return this.started && this.captureAllowed;
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.captureAllowed = false;
    this.earlyEvents = [];
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.inactiveTimer) clearTimeout(this.inactiveTimer);
    if (this.eventTimer) clearTimeout(this.eventTimer);
    this.eventTimer = null;
    this.heartbeatRequested = false;
    this.stopObservingNavigation?.();
    this.stopObservingNavigation = null;
    this.heartbeatTimer = null;
    this.inactiveTimer = null;
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('pointerdown', this.handleActivity);
    window.removeEventListener('keydown', this.handleActivity);
    window.removeEventListener('scroll', this.handleActivity);
    window.removeEventListener('touchstart', this.handleActivity);
    this.replay.stop();
  }

  private async sendHeartbeat(keepalive = false): Promise<void> {
    if (!this.started || !this.activity) return;
    if (this.sending) {
      this.heartbeatRequested = true;
      return;
    }
    if (this.eventTimer) clearTimeout(this.eventTimer);
    this.eventTimer = null;
    this.sending = true;
    // The first request only establishes eligibility. Do not collect events
    // or activity time until the backend confirms an Asian visitor location.
    const activity = this.captureAllowed
      ? this.activity.snapshotSeconds(Date.now())
      : { activeSeconds: 0, visibleSeconds: 0 };
    const eventBatch = this.captureAllowed ? events.batch() : [];
    const heartbeat: WebsiteSessionHeartbeatType = {
      sessionUuid: this.sessionUuid,
      deviceId: this.deviceUuid,
      pagePath: getWebsitePagePath(),
      platform: 'web',
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      appVersion: environment.sentry.release,
      nativeVersion: null,
      activeSeconds: activity.activeSeconds,
      visibleSeconds: activity.visibleSeconds,
      ...(eventBatch.length > 0
        ? { customer_device_session_events: eventBatch }
        : {}),
      device: {
        model: null,
        operatingSystem: navigator.platform || null,
        osVersion: null,
        screen: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language || null,
      },
      ...(this.replay.heartbeatCredentials()
        ? { replay: this.replay.heartbeatCredentials()! }
        : {}),
    };

    try {
      const response = await fetch(
        `${environment.api.phpApiUrl.replace(/\/$/, '')}/v1/external/website-telemetry`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-OB-Context': websiteBrowserContext(getDemoFingerprint()),
          },
          body: JSON.stringify({ customerDeviceSession: heartbeat }),
          keepalive,
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = (await response.json()) as WebsiteTelemetryResponseType;
      if (!this.started) return;
      if (result.enabled !== true) {
        events.clear();
        this.replay.disable();
        this.stop();
        return;
      }
      this.activity.acknowledgeSeconds(activity);
      events.acknowledge(eventBatch);
      if (events.batch(1).length > 0) this.requestHeartbeat();
      this.serverSessionUuid = result.customer_device_session_uuid;
      if (!this.captureAllowed) {
        this.captureAllowed = true;
        this.recordSessionStarted();
        this.activity = new SessionActivityAccumulator(
          Date.now(),
          !document.hidden
        );
        this.handleActivity();
        this.recordPageView();
        const early = this.earlyEvents;
        this.earlyEvents = [];
        for (const event of early) {
          this.recordEvent(event.type, event.value, event.data, event);
        }
      }
      if (!document.hidden) this.replay.resume();
      await this.replay.applyDirective(result.replay);
    } catch (error) {
      console.warn('[WebsiteSessionReplay] Telemetry heartbeat failed', error);
    } finally {
      this.sending = false;
      if (this.started && this.heartbeatRequested) {
        this.heartbeatRequested = false;
        this.requestHeartbeat();
      }
    }
  }
}

const service = new WebsiteSessionReplayService();

export function startWebsiteSessionReplay(): () => void {
  service.start();
  return () => service.stop();
}

export function getWebsiteSessionUuid(): string | null {
  return service.getSessionUuid();
}

export function trackWebsiteSessionEvent(
  type: WebsiteSessionEventNameType,
  value: string | null = null,
  data: Record<string, unknown> | null = null
): void {
  service.recordEvent(type, value, data);
}
